import { FC, useEffect } from 'react';
import Joyride, { CallBackProps, Step, TooltipRenderProps } from 'react-joyride';
import { Box, Text, Button, HStack, IconButton } from 'src/components/ui';
import { CloseIcon } from '@chakra-ui/icons';

// CSS to fix floater arrow positioning
const arrowStyles = `
.__floater__arrow span {
	position: relative !important;
	top: auto !important;
	margin-top: 22px;
	margin-bottom: 8px;
}
`;

export interface TourState {
	run: boolean;
	steps: Step[];
}

interface UseJoyrideProps {
	state: TourState;
	callback: (data: CallBackProps) => void;
}

/**
 * Custom Tooltip Component for cleaner design
 */
const CustomTooltip: FC<TooltipRenderProps> = ({
	continuous,
	index,
	step,
	backProps,
	closeProps,
	primaryProps,
	skipProps,
	tooltipProps,
	size,
}) => {
	const isLastStep = index === size - 1;

	return (
		<Box
			{...tooltipProps}
			bg="white"
			borderRadius="12px"
			boxShadow="0 10px 40px rgba(0, 0, 0, 0.15)"
			maxW="320px"
			overflow="hidden"
			position="relative"
		>
			{/* Close button - absolute positioned top-right */}
			<IconButton
				{...closeProps}
				aria-label="Close"
				icon={<CloseIcon boxSize={2.5} />}
				position="absolute"
				top={2}
				right={2}
				size="xs"
				variant="ghost"
				color="gray.400"
				minW="24px"
				h="24px"
				_hover={{ color: 'gray.600', bg: 'gray.100' }}
				zIndex={1}
			/>

			{/* Header */}
			<Box p={4} pb={2} pr={10}>
				<Text fontSize="16px" fontWeight="700" color="gray.800">
					{step.title}
				</Text>
			</Box>

			{/* Content */}
			<Box px={4} pb={4}>
				<Text fontSize="14px" color="gray.600" lineHeight="1.5">
					{step.content}
				</Text>
			</Box>

			{/* Footer with buttons */}
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				px={4}
				py={3}
				bg="gray.50"
				borderTop="1px solid"
				borderColor="gray.100"
			>
				<Button
					{...skipProps}
					variant="ghost"
					size="sm"
					color="gray.500"
					fontWeight="500"
					_hover={{ color: 'gray.700', bg: 'transparent' }}
					px={0}
				>
					Skip
				</Button>

				<HStack spacing={2}>
					{index > 0 && (
						<Button
							{...backProps}
							variant="ghost"
							size="sm"
							color="gray.600"
							fontWeight="500"
							_hover={{ bg: 'gray.100' }}
						>
							Back
						</Button>
					)}
					<Button
						{...primaryProps}
						colorScheme="orange"
						size="sm"
						fontWeight="600"
						px={4}
					>
						{isLastStep ? 'Finish' : `Next (${index + 1}/${size})`}
					</Button>
				</HStack>
			</Box>
		</Box>
	);
};

const ProductTour: FC<UseJoyrideProps> = ({ state, callback }) => {
	// Inject arrow styles on mount
	useEffect(() => {
		const styleId = 'product-tour-arrow-styles';
		if (!document.getElementById(styleId)) {
			const styleElement = document.createElement('style');
			styleElement.id = styleId;
			styleElement.textContent = arrowStyles;
			document.head.appendChild(styleElement);
		}
		return () => {
			const existingStyle = document.getElementById(styleId);
			if (existingStyle) {
				existingStyle.remove();
			}
		};
	}, []);

	return (
		<Joyride
			steps={state.steps}
			callback={callback}
			run={state.run}
			disableScrolling={true}
			disableOverlay={true}
			tooltipComponent={CustomTooltip}
			floaterProps={{
				styles: {
					floater: {
						zIndex: 10002,
					},
					arrow: {
						spread: 12,
						length: 8,
					},
				},
			}}
			styles={{
				options: {
					primaryColor: '#F6851E',
					zIndex: 10002,
					arrowColor: '#ffffff',
				},
			}}
			continuous
			showSkipButton
		/>
	);
};
export default ProductTour;
